<script setup lang="ts">
/**
 * ConsoleShowcase — Hero 产品主视觉：管理控制台高保真 mockup
 * 浏览器窗框 + 左侧菜单（足球/篮球/设置/收藏/反馈）+ 赛事导航。
 * 足球 | 篮球可点击切换，与真实控制台「赛事」分组同构。
 * 无比分原则：比赛行只展示「队伍 vs 队伍」，不出现比分（与插件一致）。
 */
import { ref, watch } from 'vue'
import YfbIcon from '@/components/YfbIcon.vue'
import type { IconName } from '@/types/icons'

type Sport = 'football' | 'basketball'

interface Chip {
  p: string
  t: string
}

interface Match {
  league: string
  home: string
  away: string
  date: string
  chips: Chip[]
}

const sport = ref<Sport>('football')
const expandedIdx = ref(0)
const activeLeague = ref('全部')

const leagueTabs: Record<Sport, string[]> = {
  football: ['全部', '世界杯', '欧冠', '英超', '西甲'],
  basketball: ['全部', 'NBA', 'CBA']
}

const areaChips: Record<Sport, string[]> = {
  football: ['国际', '国内', '第三方导航'],
  basketball: ['全部', '国内', '第三方导航']
}

const matches: Record<Sport, Match[]> = {
  football: [
    {
      league: '世界杯小组赛',
      home: '巴西',
      away: '法国',
      date: '11-25 03:00',
      chips: [
        { p: '央视', t: '全场' },
        { p: '央视', t: '上半场' },
        { p: '咪咕', t: '官方集锦' }
      ]
    },
    { league: '欧冠 1/4 决赛', home: '皇家马德里', away: '曼城', date: '11-26 03:00', chips: [] },
    { league: '英超第 15 轮', home: '利物浦', away: '阿森纳', date: '11-23 20:30', chips: [] }
  ],
  basketball: [
    {
      league: 'NBA 季后赛',
      home: '湖人',
      away: '勇士',
      date: '04-14 10:30',
      chips: [
        { p: '腾讯', t: '全场' },
        { p: '腾讯', t: '第一节' },
        { p: '咪咕', t: '官方集锦' }
      ]
    },
    { league: 'CBA 总决赛', home: '辽宁', away: '广东', date: '04-13 19:35', chips: [] },
    { league: 'NBA 常规赛', home: '凯尔特人', away: '独行侠', date: '04-12 08:00', chips: [] }
  ]
}

const sideTools: { icon: IconName; label: string }[] = [
  { icon: 'lucide:settings', label: '设置' },
  { icon: 'lucide:star', label: '收藏' },
  { icon: 'lucide:message-square', label: '反馈' }
]

watch(sport, () => {
  activeLeague.value = '全部'
  expandedIdx.value = 0
})

function toggleExpand(idx: number) {
  expandedIdx.value = expandedIdx.value === idx ? -1 : idx
}
</script>

<template>
  <div class="console" role="img" aria-label="隐分宝管理控制台界面演示">
    <!-- 浏览器窗框 -->
    <div class="console__chrome">
      <span class="console__dot" aria-hidden="true"></span>
      <span class="console__dot" aria-hidden="true"></span>
      <span class="console__dot" aria-hidden="true"></span>
      <span class="console__url">
        <YfbIcon name="lucide:lock" :size="10" />
        <span>yfb://console/{{ sport }}</span>
      </span>
    </div>

    <div class="console__body">
      <!-- 左侧菜单 -->
      <aside class="console__side">
        <div class="console__side-group">
          <span class="console__side-title">赛事</span>
          <button
            type="button"
            class="console__side-item"
            :class="{ 'console__side-item--active': sport === 'football' }"
            :aria-pressed="sport === 'football'"
            @click="sport = 'football'"
          >
            <YfbIcon name="ph:soccer-ball-bold" :size="12" />
            <span>足球</span>
          </button>
          <button
            type="button"
            class="console__side-item"
            :class="{ 'console__side-item--active': sport === 'basketball' }"
            :aria-pressed="sport === 'basketball'"
            @click="sport = 'basketball'"
          >
            <YfbIcon name="ph:basketball-bold" :size="12" />
            <span>篮球</span>
          </button>
        </div>
        <div class="console__side-group">
          <span class="console__side-title">工具</span>
          <span v-for="tool in sideTools" :key="tool.label" class="console__side-item console__side-item--static">
            <YfbIcon :name="tool.icon" :size="12" />
            <span>{{ tool.label }}</span>
          </span>
        </div>
      </aside>

      <!-- 赛事导航主区 -->
      <div class="console__main">
        <div class="console__areas">
          <span
            v-for="(area, idx) in areaChips[sport]"
            :key="area"
            class="console__area"
            :class="{ 'console__area--active': idx === 0 }"
          >{{ area }}</span>
        </div>

        <div class="console__leagues">
          <button
            v-for="l in leagueTabs[sport]"
            :key="l"
            type="button"
            class="console__league"
            :class="{ 'console__league--active': activeLeague === l }"
            :aria-pressed="activeLeague === l"
            @click="activeLeague = l"
          >{{ l }}</button>
        </div>

        <div class="console__list">
          <div
            v-for="(m, idx) in matches[sport]"
            :key="`${sport}-${idx}`"
            class="console__row"
            :class="{ 'console__row--open': expandedIdx === idx }"
          >
            <button
              type="button"
              class="console__head"
              :aria-expanded="expandedIdx === idx"
              @click="toggleExpand(idx)"
            >
              <span class="console__row-league">{{ m.league }}</span>
              <span class="console__row-teams">
                <span>{{ m.home }}</span>
                <span class="console__row-vs">VS</span>
                <span>{{ m.away }}</span>
              </span>
              <span class="console__row-date">{{ m.date }}</span>
              <YfbIcon
                :name="expandedIdx === idx ? 'lucide:chevron-down' : 'lucide:chevron-right'"
                :size="12"
                class="console__row-arrow"
              />
            </button>
            <div v-if="expandedIdx === idx && m.chips.length" class="console__chips">
              <span v-for="chip in m.chips" :key="chip.t" class="console__chip">
                <span class="console__chip-p">{{ chip.p }}</span>·<span>{{ chip.t }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="console__footer">
      <YfbIcon name="lucide:shield-check" :size="11" />
      <span>隐分宝 · 比分已隐藏 · 视频来源一览无余</span>
    </div>
  </div>
</template>

<style scoped>
.console {
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  background-color: var(--yfb-surface);
  box-shadow: var(--yfb-shadow-3);
  overflow: hidden;
  font-size: 11px;
}

/* 浏览器窗框 */
.console__chrome {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background-color: var(--yfb-surface-2);
  border-bottom: 1px solid var(--yfb-rule);
}

.console__dot {
  width: 8px;
  height: 8px;
  border: 1px solid var(--yfb-rule);
  border-radius: 2px;
  background-color: var(--yfb-surface);
}

.console__url {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 8px;
  padding: 3px 10px;
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  color: var(--yfb-text-secondary);
  font-family: var(--yfb-mono);
  font-size: 10px;
}

/* 布局 */
.console__body {
  display: grid;
  grid-template-columns: 92px 1fr;
  min-height: 264px;
}

.console__side {
  padding: 10px 8px;
  background-color: var(--yfb-surface-2);
  border-right: 1px solid var(--yfb-rule);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.console__side-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.console__side-title {
  padding: 0 8px;
  font-family: var(--yfb-font-heading);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  color: var(--yfb-accent-2);
}

.console__side-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  background: transparent;
  border: none;
  border-radius: var(--yfb-radius-sm);
  color: var(--yfb-text-secondary);
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out;
}

.console__side-item:hover {
  color: var(--yfb-ink);
  background-color: var(--yfb-surface);
}

.console__side-item--active {
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  box-shadow: inset 2px 0 0 var(--yfb-accent);
}

.console__side-item--static {
  cursor: default;
}

/* 主区 */
.console__main {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.console__areas,
.console__leagues {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.console__area,
.console__league {
  padding: 3px 8px;
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  color: var(--yfb-text-secondary);
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.console__area {
  cursor: default;
}

.console__area--active {
  background-color: var(--yfb-accent);
  border-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

.console__league {
  cursor: pointer;
  font-family: inherit;
  transition: border-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out;
}

.console__league:hover,
.console__league--active {
  border-color: var(--yfb-accent);
  color: var(--yfb-accent);
}

/* 比赛行 */
.console__list {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-md);
  overflow: hidden;
}

.console__row + .console__row {
  border-top: 1px solid var(--yfb-rule-soft);
}

.console__head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: inherit;
  color: var(--yfb-ink);
  text-align: left;
  transition: background-color var(--yfb-anim-fast) ease-out;
}

.console__head:hover {
  background-color: var(--yfb-surface-2);
}

.console__row-league {
  flex-shrink: 0;
  max-width: 88px;
  padding: 2px 6px;
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  color: var(--yfb-accent);
  font-family: var(--yfb-font-heading);
  font-size: 9px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.console__row-teams {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
}

.console__row-vs {
  padding: 1px 5px;
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  color: var(--yfb-accent);
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
}

.console__row-date {
  flex-shrink: 0;
  color: var(--yfb-text-secondary);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-variant-numeric: tabular-nums;
}

.console__row-arrow {
  flex-shrink: 0;
  color: var(--yfb-text-secondary);
}

.console__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 10px;
  background-color: var(--yfb-surface-2);
  border-top: 1px solid var(--yfb-rule-soft);
}

.console__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  color: var(--yfb-ink);
  font-size: 10px;
}

.console__chip-p {
  color: var(--yfb-accent);
  font-weight: 700;
}

/* footer */
.console__footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: var(--yfb-surface-2);
  border-top: 1px solid var(--yfb-rule);
  color: var(--yfb-accent);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: var(--yfb-ls-mono);
}

@media (max-width: 767px) {
  .console__body {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .console__side {
    flex-direction: row;
    align-items: center;
    gap: 10px;
    border-right: none;
    border-bottom: 1px solid var(--yfb-rule);
    overflow-x: auto;
  }

  .console__side-group {
    flex-direction: row;
    align-items: center;
    gap: 2px;
  }

  .console__row-date {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .console__head,
  .console__side-item,
  .console__league {
    transition: none;
  }
}
</style>
